import { ImageScaleView } from '@play-co/timestep-core/ui';
import ButtonScaleView, {
  ButtonScaleViewOpts,
} from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import createIntervalEmitter from 'src/lib/createIntervalEmitter';
import { formatDuration } from 'src/lib/formatDuration';
import {
  getCurrentTeamEventEndDate,
  getCurrentTeamEventId,
  getCurrentTeamEventSchedule,
  isTeamFeatureUnlocked,
} from 'src/replicant/team/team.getters';
import features from 'src/replicant/ruleset/features';
import { captureException } from '@sentry/browser';
import { TeamEventType } from 'src/replicant/team/team.ruleset';
import { assertNever } from 'src/replicant/utils';
import i18n from 'src/lib/i18n/i18n';
import {
  getCurrentTeamCompetition,
  getUnclaimedTeamCompetition,
} from '../team.sequence';

export class TeamCompetitionButton extends ButtonScaleView {
  private badge: ImageScaleView;
  private timerFrame: ImageScaleView;
  private eventType?: TeamEventType;
  private autoUpdating = false;

  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 146,
      height: 154,
      pressedOffsetY: 5,
      ...opts,
    });

    this.badge = new ImageScaleView({
      superview: this,
      x: this.style.width * 0.5,
      y: 80,
      width: 136,
      height: 136,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      image: 'assets/ui/match2/team/competition/icon_teamCompetition_sm.png',
    });

    this.timerFrame = new ImageScaleView({
      superview: this.badge,
      width: 134,
      height: 42,
      image: 'assets/ui/match2/team/competition/comp_btn_frame.png',
      centerOnOrigin: true,
      x: this.badge.style.width * 0.5,
      y: this.badge.style.height - 10,
    });

    const timer = new LangBitmapFontTextView({
      superview: this.timerFrame,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      font: 'Body',
      width: this.timerFrame.style.width,
      height: this.timerFrame.style.height,
      x: -1,
      y: -4,
    });

    // Starts hidden, will show up after update, if available
    this.hide();

    createIntervalEmitter(({ user }, now) => ({ user, now })).addListener(
      ({ user, now }) => {
        if (!this.autoUpdating) return;

        try {
          // Feature not unlocked, bail.
          if (
            !features(user).teamsTournaments ||
            !isTeamFeatureUnlocked(user)
          ) {
            this.hide();
            return;
          }

          // Trigger fetching promise. This will update the cache.
          void getUnclaimedTeamCompetition()?.get();

          // Check if there is an unclaimed reward. Get the cached value.
          const unclaimed = getUnclaimedTeamCompetition()?.getCached();

          // If there's an unclaimed event, show the button with the same event type.
          if (unclaimed && unclaimed.length > 0) {
            const unclaimedEvent = unclaimed[0]!;
            this.showAsType(unclaimedEvent.type);
            timer.localeText = () => i18n('match2.dailyBonus.collect');
            return;
          }

          // No active event, bail.
          const currentEventId = getCurrentTeamEventId(now);
          if (!currentEventId) {
            this.hide();
            return;
          }

          // Trigger fetching promise. This will update the cache.
          void getCurrentTeamCompetition()?.get();

          // Get team profile. Use the cached value.
          const competition = getCurrentTeamCompetition()?.getCached();

          // Hide if the competition is already claimed.
          if (
            competition?.status === 'complete-already-claimed' ||
            competition?.status === 'complete-no-contribution'
          ) {
            this.hide();
            return;
          }

          // Check if user is in a team and team can join current event.
          if (!competition || !competition.canJoin) {
            this.hide();
            return;
          }

          // If there's no team, or the team can join the event, show the button.
          const currentEvent = getCurrentTeamEventSchedule(now);
          this.showAsType(currentEvent!.type);
          timer.localeText = () =>
            formatDuration((getCurrentTeamEventEndDate(now) ?? 0) - now);
        } catch (e) {
          captureException(e);
          this.hide();
        }
      },
    );
  }

  public autoUpdate() {
    this.autoUpdating = true;
  }

  public stopAutoUpdate() {
    this.autoUpdating = false;
  }

  private showAsType(type: TeamEventType) {
    this.eventType = type;
    switch (type) {
      case 'competition':
        this.showAsCompetition();
        break;
      case 'chest':
        this.showAsChest();
        break;
      default:
        assertNever(type);
    }
    this.show();
  }

  private showAsCompetition() {
    this.badge.updateOpts({
      image: 'assets/ui/match2/team/competition/icon_teamCompetition_sm.png',
      width: 136,
      height: 136,
    });
    this.timerFrame.updateOpts({
      image: 'assets/ui/match2/team/competition/comp_btn_frame.png',
    });
  }

  private showAsChest() {
    this.badge.updateOpts({
      image: 'assets/ui/match2/team/chest/team_chest_icon.png',
      width: 145,
      height: 147,
    });
    this.timerFrame.updateOpts({
      image: 'assets/ui/match2/team/chest/team_chest_frame.png',
    });
  }

  public getTeamEventType() {
    return this.eventType;
  }
}
